/**
 * ENTERPRISE V2: Pure AuthService-based Context
 * No legacy dependencies, no bridge logic
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { authService } from '../services/auth/AuthService';
import { validateAdminTokenOnAppMount } from '../services/auth/adminTokenGuard';
import { StorageAdapter } from '../services/auth/StorageAdapter';
import type { User, AuthState, LoginCredentials } from '../services/auth/types';
import { SocketManager } from '../services/SocketManager';
import { getWsBaseUrl, isLoginHostForClient } from '../config/runtimeUrls';

interface AuthContextValue {
  state: AuthState;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  clearError: () => void;
  updateUserPartial: (partial: Partial<User>) => void;
}

const AuthContextV2 = createContext<AuthContextValue | undefined>(undefined);

export const AuthProviderV2: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>('INITIALIZING');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const socketInitRef = useRef(false);

  useEffect(() => {
    const initialize = async () => {
      console.log('🔐 AuthContextV2: Initializing...');

      if (!validateAdminTokenOnAppMount()) {
        setState('UNAUTHENTICATED');
        setUser(null);
        setInitialized(true);
        return;
      }

      const isCentralLoginRoute = (() => {
        if (typeof window === 'undefined') return false;
        if (!isLoginHostForClient()) return false;
        const path = String(window.location.pathname || '').toLowerCase();
        const isAuthContinue = path.startsWith('/auth/continue');
        return !isAuthContinue;
      })();

      if (isCentralLoginRoute) {
        // Strict security boundary for central login host:
        // do not auto-restore prior sessions from persisted storage/cookies.
        authService.forceUnauthenticated('central_login_bootstrap');
        setState('UNAUTHENTICATED');
        setUser(null);
        setInitialized(true);
        return;
      }

      // 🔥 HMR-Safe: Sync state immediately if already authenticated
      if (authService.isAuthenticated()) {
        console.log('🔐 AuthContextV2: Already authenticated (HMR preserved), syncing state...');
        setState(authService.getState());
        setUser(authService.getUser());
        setInitialized(true);
        return;
      }

      const storageAdapter = new StorageAdapter();
      storageAdapter.migrateLegacyKeys();

      const result = await authService.initialize();
      console.log('🔐 AuthContextV2: Initialize result:', result);

      setInitialized(true);

      console.log(
        '🔐 AuthContextV2: Initialization complete, isAuthenticated:',
        authService.isAuthenticated()
      );
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!initialized) return;

    const unsubscribe = authService.subscribe((newState, newUser) => {
      setState(newState);
      setUser(newUser);
      console.log('🔐 AuthContextV2: State updated', { state: newState, userId: newUser?.id });
    });

    return unsubscribe;
  }, [initialized]);

  useEffect(() => {
    if (!initialized) return;

    if (state === 'AUTHENTICATED') {
      if (!socketInitRef.current) {
        SocketManager.init({
          url: getWsBaseUrl(),
          auth: () => ({
            token: authService.getAccessToken() || '',
          }),
        });
        socketInitRef.current = true;
      }
      return;
    }

    if (socketInitRef.current) {
      SocketManager.destroy();
      socketInitRef.current = false;
    }
  }, [initialized, state]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      console.log('🔐 AuthContextV2: Login attempt', { email });

      const result = await authService.login({ email, password });

      if (!result.success) {
        setError(result.error || 'Login failed');
        throw new Error(result.error || 'Login failed');
      }

      console.log('🔐 AuthContextV2: Login successful, redirecting to dashboard');
      navigate('/dashboard');
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    console.log('🔐 AuthContextV2: Logout');
    setError(null);

    await authService.logout();
    navigate('/login');
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    // IMPORTANT:
    // "refreshUser" is used across the app as a *state sync* mechanism (especially after redirects).
    // It must NOT force a token refresh, because that can log users out if a refresh token isn't available.
    console.log('🔐 AuthContextV2: Refresh user (sync state)');

    // If AuthService is already authenticated, just sync the context state immediately.
    if (authService.isAuthenticated()) {
      setState(authService.getState());
      setUser(authService.getUser());
      return;
    }

    // Otherwise, attempt to rehydrate from storage (initialize will refresh only if token is expired).
    await authService.initialize();
    setState(authService.getState());
    setUser(authService.getUser());
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;

      // Super admin has all permissions
      if (user.role === 'super_admin') return true;

      // Check if user has this specific permission
      return user.permissions?.includes(permission) || false;
    },
    [user]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateUserPartial = useCallback(
    (partial: Partial<User>) => {
      setUser(prev => (prev ? ({ ...prev, ...partial } as User) : prev));

      // Update storage
      if (user) {
        const updatedUser = { ...user, ...partial };
        const storageAdapter = new StorageAdapter();
        storageAdapter.setUser(updatedUser);
      }
    },
    [user]
  );

  const isAuthenticated = state === 'AUTHENTICATED';
  const isLoading =
    state === 'INITIALIZING' || state === 'AUTHENTICATING' || state === 'REFRESHING';

  const value: AuthContextValue = {
    state,
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
    hasPermission,
    clearError,
    updateUserPartial,
  };

  return <AuthContextV2.Provider value={value}>{children}</AuthContextV2.Provider>;
};

// Export as useAuth for compatibility with all components
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContextV2);

  if (!context) {
    throw new Error('useAuth must be used within AuthProviderV2');
  }

  return context;
};

// Keep useAuthV2 as alias for gradual migration
export const useAuthV2 = useAuth;
